const ASSET_COUNTERPART_TYPES = [
    { value: "software", label: "Software", kind: "asset" },
    { value: "hardware", label: "Hardware", kind: "asset" },
];

export const DIRECTION_RECEIVES = "receives";
export const DIRECTION_SENDS = "sends";
export const RELATION_SYNCS = "syncs";

export const isAssetCounterpartType = (type) =>
    type === "software" || type === "hardware";

export const isEndpointCounterpartType = (type) =>
    Boolean(type) && !isAssetCounterpartType(type);

export const isHardwareCounterpart = (counterpart) =>
    counterpart?.kind === "asset" && counterpart?.type === "hardware";

export const isReceivesFromHardware = (direction, counterpart) =>
    direction === DIRECTION_RECEIVES && isHardwareCounterpart(counterpart);

export const getSyncsRelationOption = (options = []) =>
    (options || []).find((option) => option.value === RELATION_SYNCS) || null;

export const withRelationOptionAvailability = (
    options = [],
    { onlySyncs = false } = {}
) => {
    if (!onlySyncs) {
        return options;
    }

    return (options || []).map((option) => ({
        ...option,
        isDisabled: option.value !== RELATION_SYNCS,
    }));
};

export const resolveHardwareRelation = (
    direction,
    counterpart,
    currentRelation,
    relationOptions = []
) => {
    if (!isHardwareCounterpart(counterpart)) {
        return null;
    }

    if (!isReceivesFromHardware(direction, counterpart)) {
        return currentRelation;
    }

    if (currentRelation?.value === RELATION_SYNCS) {
        return currentRelation;
    }

    return getSyncsRelationOption(relationOptions);
};

export const mapEndpointToCounterpart = (endpoint) => ({
    kind: "endpoint",
    id: endpoint.id,
    type: endpoint.type,
    name: endpoint.name,
    identifier: endpoint.identifier || null,
});

const ENDPOINT_SECOND_FIELD_LABELS = {
    website: "URL",
    mailbox: "Email Address",
    external_system: "Description",
    database: "Identifier",
    file_storage: "Path / URL",
    other: "Identifier",
};

export const getEndpointFieldLabels = (type) => ({
    name: "Name",
    nameRequired: true,
    identifier: ENDPOINT_SECOND_FIELD_LABELS[type] || "Identifier",
    identifierOptional: type === "external_system",
});

export const buildCounterpartTypeOptions = (endpointTypes = []) => {
    return [
        ...ASSET_COUNTERPART_TYPES,
        ...(endpointTypes || []).map((option) => ({
            value: option.value,
            label: option.label || option.value,
            kind: "endpoint",
        })),
    ];
};

export const toSelectOptions = (items = [], t) => {
    return (items || []).map((item) => ({
        value: item.value,
        label: t(item.label || item.value),
    }));
};

export const toSelectedOptions = (values = [], options = []) => {
    return (values || [])
        .map((value) => options.find((option) => option.value === value))
        .filter(Boolean);
};

export const mapAssetToCounterpart = (asset, type) => ({
    kind: "asset",
    id: asset.id,
    type,
    name: asset.name,
});

export const normalizeId = (value) => {
    if (value == null || value === "") {
        return null;
    }

    const id = Number(value);

    return Number.isFinite(id) ? id : null;
};

export const trimToNull = (value) => {
    const trimmed = String(value ?? "").trim();

    return trimmed ? trimmed : null;
};

export const toOptionValues = (options = []) => {
    return (Array.isArray(options) ? options : [])
        .map((option) => option?.value)
        .filter((value) => value != null && value !== "");
};

export const resolveDetailsWrapperAssetId = (details) => {
    if (!details) {
        return null;
    }

    return (
        normalizeId(details.asset_id) ??
        normalizeId(details.assetId) ??
        normalizeId(details.asset?.id) ??
        normalizeId(details.gd_asset_id)
    );
};

const assetLinkedSoftwareId = (asset) =>
    normalizeId(asset?.software_id) ??
    normalizeId(asset?.softwareId) ??
    normalizeId(asset?.software?.id) ??
    normalizeId(asset?.module_id) ??
    normalizeId(asset?.assetable_id);

export const findWrapperAssetId = (assets = [], softwareId, softwareName) => {
    const currentSoftwareId = normalizeId(softwareId);
    const rows = Array.isArray(assets) ? assets : [];

    if (currentSoftwareId != null) {
        const bySoftwareId = rows.find(
            (asset) => assetLinkedSoftwareId(asset) === currentSoftwareId
        );

        if (bySoftwareId) {
            return normalizeId(bySoftwareId.id);
        }
    }

    const name = String(softwareName || "")
        .trim()
        .toLowerCase();

    if (!name) {
        return null;
    }

    const byName = rows.find(
        (asset) => String(asset?.name || "").trim().toLowerCase() === name
    );

    return normalizeId(byName?.id);
};

export const buildCreatePayload = ({
    direction,
    wrapperAssetId,
    counterpart,
    purpose,
    notes,
    dataTypes,
    dataCategories,
    methods,
    relation,
}) => {
    const currentSide = {
        kind: "asset",
        id: normalizeId(wrapperAssetId),
    };
    const counterpartSide = {
        kind: counterpart.kind,
        id: normalizeId(counterpart.id),
    };
    const payload = {
        source:
            direction === DIRECTION_RECEIVES ? counterpartSide : currentSide,
        destination:
            direction === DIRECTION_RECEIVES ? currentSide : counterpartSide,
        purpose: trimToNull(purpose),
        notes: trimToNull(notes),
        data_types: dataTypes,
        data_categories: dataCategories,
        methods,
    };

    if (isHardwareCounterpart(counterpart) && relation) {
        payload.relation = relation;
    } else if (relation === null) {
        payload.relation = null;
    }

    return payload;
};
